import React, { useState, useMemo, memo } from "react";
import { Paper, Grid, Typography, IconButton } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import { delay } from "lodash";
import {
  sortTypeOptions,
  ssPogSortByOptions,
  ssPogFilterByOptions,
} from "../../../utils";
import useStableCallback from "../../../hooks/useStableCallback";
import {
  setPogFilters,
  resetPogFilters,
  showNotifier,
} from "../../../store/globalSlice";
import CustomInput from "../../../components/CustomInput";
import CustomDropdown from "../../../components/CustomDropdown";
import CustomDateRangePicker from "../../../components/CustomDateRangePicker";
import CustomAutoComplete from "../../../components/CustomAutoComplete";
import useRequest from "../../../hooks/useRequest";
import configData from "../../../config.js";

const INPUT_REQUIRED_FILTER_INDEX = ["0", "1", "2", "3"];

const RELEASE_DATE_FILTER_INDEX = "16";

export const SKU_AUTOCOMPLETE_INDEX = "4";

export const VENDOR_AUTOCOMPLETE_INDEX = "5";

const PaperSx = {
  pl: 1,
  "& .MuiTypography-root": {
    fontSize: "0.75rem",
    fontWeight: 500,
  },
};

const CenterAlignSx = { display: "flex", alignItems: "center" };

const FlexEndSx = { ...CenterAlignSx, justifyContent: "flex-end" };

const CustomDropdownSx = { fontSize: "10px", mx: 1 };

const HelperTextSx = { mt: 0, height: 0, fontSize: "0.6rem", fontWeight: 500 };

const ErrorSx = { border: "1px solid red", borderRadius: "3px" };

const INIT_ERRORS = {
  vendorName: "",
  sku: "",
};

const SSPogFilter = ({
  disableFilters = false,
  handleFilter,
  handleReset,
  handleSortChange,
  handleSKUFilter,
  handleVendorFilter,
}) => {
  const dispatch = useDispatch();
  const { requestMaker } = useRequest();
  const { id: storeId } = useSelector((store) => store.global.store);
  const { filterBy, filterValue, startDate, endDate, sortBy, sortType } =
    useSelector((store) => store.global.pogFilters);
  const [releaseDateError, setReleaseDateError] = useState(false);

  const [errors, setErrors] = useState(INIT_ERRORS);
  const [vendorInputValue, setVendorInputValue] = useState("");
  const [vendorOptions, setVendorOptions] = useState([]);
  const [skuInputValue, setSkuInputValue] = useState("");
  const [skuOptions, setSkuOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  const getAutoCompleteOptions = useStableCallback(
    (pogValue = null, vendorName = null, skuNumber = null) => {
      setOptionsLoading(true);
      requestMaker({
        method: "post",
        url: configData.GetDeptFilters,
        data: {
          PlanogramValue: pogValue,
          VendorName: vendorName,
          SkuNumber: skuNumber,
          StoreId: storeId,
          IsStoreSupplies: true,
        },
      })
        .then((res) => {
          const list = typeof res?.data === "object" ? res?.data : [];
          if (!!vendorName) {
            setVendorOptions([...list]);
          }
          if (!!skuNumber) {
            setSkuOptions([...list]);
          }
        })
        .catch((err) =>
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          )
        )
        .finally(() => setOptionsLoading(false));
    }
  );

  const handleFilterBy = useStableCallback((e) => {
    dispatch(resetPogFilters());
    setReleaseDateError(false);
    setErrors(INIT_ERRORS);
    dispatch(
      setPogFilters({
        field: "filterBy",
        value: e?.target?.value,
        prevViewBy: filterBy, // It will backtrace prev value so if any non async filter selection will trigger list fetch.
      })
    );
    dispatch(
      setPogFilters({
        field: "filterValue",
        value: "",
      })
    );
  });

  const handleFilterValue = useStableCallback((e) => {
    dispatch(setPogFilters({ field: "filterValue", value: e?.target?.value }));
  });

  const handleSortBy = useStableCallback((e) => {
    dispatch(setPogFilters({ field: "sortBy", value: e?.target?.value }));
    // throttle for sorting
    delay(handleSortChange);
  });

  const handleSortType = useStableCallback((e) => {
    dispatch(setPogFilters({ field: "sortType", value: e?.target?.value }));
    // throttle for sorting
    delay(handleSortChange);
  });

  const handleFilterApply = useStableCallback((_e) => {
    if (filterBy === SKU_AUTOCOMPLETE_INDEX) {
      setReleaseDateError(false);
      setErrors(INIT_ERRORS);
      handleSKUFilter();
    } else if (filterBy === VENDOR_AUTOCOMPLETE_INDEX) {
      setReleaseDateError(false);
      setErrors(INIT_ERRORS);
      handleVendorFilter();
    } else {
      handleFilter();
    }
  });

  const handleFilterReset = useStableCallback((_e) => {
    dispatch(resetPogFilters({ allReset: true }));
    setErrors(INIT_ERRORS);
    setReleaseDateError(false);
    setVendorInputValue("");
    setVendorOptions([]);
    setSkuInputValue("");
    setSkuOptions([]);
    setOptionsLoading(false);
    delay(() => handleReset(filterBy), 350);
  });

  const checkDateError = useStableCallback((error) => {
    setReleaseDateError(error);
  });

  const handleSkuValueChange = useStableCallback((_e, newValue) => {
    dispatch(
      setPogFilters({ field: "filterValue", value: newValue, skuFilter: true })
    );
  });

  const handleSkuInputChange = useStableCallback((_e, newInputValue) => {
    const inputArray = newInputValue.split("-");
    setSkuInputValue(newInputValue);
    if (!newInputValue) {
      setErrors({ ...errors, sku: "This field is required." });
    } else {
      setErrors({ ...errors, sku: "" });
      if (inputArray.length < 3 && isNaN(Number(newInputValue))) {
        setErrors({ ...errors, sku: "Please enter a valid SKU number." });
      } else {
        const value = inputArray.length > 1 ? inputArray[2] : newInputValue;
        setErrors({ ...errors, sku: "" });
        if (String(value).length >= 5) {
          getAutoCompleteOptions(null, null, Number(value));
        }
      }
    }
  });

  const handleVendorNameChange = useStableCallback((_e, newValue) => {
    dispatch(
      setPogFilters({
        field: "filterValue",
        value: newValue,
        vendorFilter: true,
      })
    );
  });

  const handleVendorInputChange = useStableCallback((e, newInputValue) => {
    setVendorInputValue(newInputValue);
    if (!newInputValue) {
      setErrors({ ...errors, vendorName: "This field is required." });
    } else {
      setErrors({ ...errors, vendorName: "" });
      if (newInputValue.length >= 3) {
        getAutoCompleteOptions(null, newInputValue, null);
      }
    }
  });

  const disableSearchAction = useMemo(
    () =>
      disableFilters ||
      !filterBy ||
      (INPUT_REQUIRED_FILTER_INDEX.includes(filterBy) &&
        !filterValue?.trim()?.length) ||
      (SKU_AUTOCOMPLETE_INDEX === filterBy && !filterValue?.trim()?.length) ||
      (VENDOR_AUTOCOMPLETE_INDEX === filterBy &&
        !filterValue?.trim()?.length) ||
      (RELEASE_DATE_FILTER_INDEX === filterBy && (!startDate || !endDate)) ||
      !!releaseDateError,
    [
      disableFilters,
      filterBy,
      filterValue,
      startDate,
      endDate,
      releaseDateError,
    ]
  );

  return (
    <Paper
      sx={{
        ...PaperSx,
        ...(!!releaseDateError && { pb: 3.5 }),
        height:
          errors?.sku || errors?.vendorName || !!releaseDateError
            ? "45px"
            : "auto",
      }}
    >
      <Grid container spacing={0.5}>
        <Grid item xs={7.7} sx={CenterAlignSx}>
          <Typography variant="caption" fontWeight={500} lineHeight="unset">
            Search POG By:
          </Typography>
          <CustomDropdown
            inputsx={CustomDropdownSx}
            value={filterBy}
            onChange={handleFilterBy}
            disabled={disableFilters}
          >
            <option aria-label="None" value="">
              None
            </option>
            {Object.keys(ssPogFilterByOptions).map((optionIndex) => (
              <option key={optionIndex} value={optionIndex}>
                {ssPogFilterByOptions[optionIndex].label}
              </option>
            ))}
          </CustomDropdown>
          {INPUT_REQUIRED_FILTER_INDEX.includes(filterBy) && (
            <CustomInput
              value={filterValue}
              onChange={handleFilterValue}
              disabled={disableFilters}
              inputProps={{
                maxlength: 250,
              }}
              sx={{ mr: 1 }}
            />
          )}
          {RELEASE_DATE_FILTER_INDEX === filterBy && (
            <CustomDateRangePicker
              isErrorPresent={checkDateError}
              showInlineError
            />
          )}
          {filterBy === SKU_AUTOCOMPLETE_INDEX && (
            <Grid item xs="auto" sx={CenterAlignSx} mr={1}>
              <Typography mr={1}>SKU *:</Typography>
              <FormControl>
                <CustomAutoComplete
                  value={filterValue}
                  disabled={disableFilters}
                  inputValue={skuInputValue}
                  options={skuOptions}
                  loading={optionsLoading}
                  onChange={handleSkuValueChange}
                  onInputChange={handleSkuInputChange}
                  inputSx={{ width: "11rem" }}
                  inputProps={{ maxLength: 6 }}
                  customSx={{ ...(!!errors?.sku && ErrorSx) }}
                />
                <FormHelperText
                  error={!!errors?.sku}
                  sx={{ ...HelperTextSx, ml: 0 }}
                >
                  {errors?.sku}
                </FormHelperText>
              </FormControl>
              <Typography
                ml={0.5}
                sx={{ color: (theme) => theme.palette.grey[500] }}
              >
                (Enter min. 5 characters)
              </Typography>
            </Grid>
          )}
          {filterBy === VENDOR_AUTOCOMPLETE_INDEX && (
            <Grid item xs="auto" sx={CenterAlignSx} mr={1}>
              <Typography mr={1}>Vendor *:</Typography>
              <FormControl>
                <CustomAutoComplete
                  value={filterValue}
                  disabled={disableFilters}
                  inputValue={vendorInputValue}
                  loading={optionsLoading}
                  options={vendorOptions}
                  onChange={handleVendorNameChange}
                  onInputChange={handleVendorInputChange}
                  inputSx={{ width: "11rem" }}
                  customSx={{ ...(!!errors?.vendorName && ErrorSx) }}
                />
                <FormHelperText
                  error={!!errors?.vendorName}
                  sx={{ ...HelperTextSx, ml: 0 }}
                >
                  {errors?.vendorName}
                </FormHelperText>
              </FormControl>
              <Typography
                ml={0.5}
                sx={{ color: (theme) => theme.palette.grey[500] }}
              >
                (Enter min. 3 characters)
              </Typography>
            </Grid>
          )}
          <IconButton
            aria-label="search"
            color="primary"
            size="small"
            onClick={handleFilterApply}
            disabled={disableSearchAction}
            sx={{ px: 0, pr: 1 }}
          >
            <SearchIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label="reset"
            color="primary"
            size="small"
            onClick={handleFilterReset}
            disabled={disableFilters}
            sx={{ px: 0 }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Grid>
        <Grid item xs={4.3} sx={FlexEndSx}>
          <Typography variant="caption" fontWeight={500} lineHeight="unset">
            Sort By:
          </Typography>
          <CustomDropdown
            inputsx={CustomDropdownSx}
            value={sortBy}
            onChange={handleSortBy}
            disabled={disableFilters}
          >
            {Object.keys(ssPogSortByOptions).map((optionIndex) => (
              <option key={optionIndex} value={optionIndex}>
                {ssPogSortByOptions[optionIndex].label}
              </option>
            ))}
          </CustomDropdown>
          <Typography
            variant="caption"
            fontWeight={500}
            lineHeight="unset"
            sx={{ ml: 1 }}
          >
            Sort Type:
          </Typography>
          <CustomDropdown
            inputsx={CustomDropdownSx}
            value={sortType}
            onChange={handleSortType}
            disabled={disableFilters}
          >
            {sortTypeOptions?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </CustomDropdown>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default memo(SSPogFilter);
